import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';
import { IFiltersSliceState } from '../../DrilldownDashboardContainer/filters/filtersSlice';
import { IPieChartDataPoint } from '../../SingleSurveyDashboardContainer/PieChartComponent';
import { RootState } from '../store';
import { buildFiltersSubtitle, buildHTMLFiltersSubtitle } from '../utils';
import { getCommentMoods } from './commentMoodsAPI';

export interface ICommentMoodsAPIResponse {
  values: IMoodResponseValues[];
  segmentName: string;
  segmentCategory: string;
  segment: string;
  question: string;
  periodLabel: string;
  surveyLabel: string;
  avidLabel: string;
  commentType: string;
}

interface ICommentMoodState {
  isLoading: boolean;
  apiResponse: ICommentMoodsAPIResponse;
  dataPoints: IPieChartDataPoint[]
  chartSubtitle: string
  dashboardSubtitle: string
}

interface IMoodResponseValues {
  label: string
  value: number
  surveyCount: number
  percent: number
}

const initialState: ICommentMoodState = {
  isLoading: false,
  apiResponse: {
    values: [],
    segmentName: '',
    segmentCategory: '',
    segment: '',
    question: '',
    periodLabel: '',
    surveyLabel: '',
    avidLabel: '',
    commentType: '',
  },
  dataPoints: [],
  chartSubtitle: '',
  dashboardSubtitle: '',
}

export const loadCommentMoods = createAsyncThunk(
  'commentMoods/get',
  async (filters: IFiltersSliceState) => {
    return getCommentMoods(filters);
  }
);

export const commentMoodsSlice = createSlice({
  name: 'commentMoods',
  initialState,
  reducers: {
    setHTMLSubtitle: (state: ICommentMoodState, action) => {
      const apiResponse = action.payload;
      if (!isEqual(state, apiResponse)) {
        state.apiResponse = apiResponse;
      }
      return state;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadCommentMoods.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(loadCommentMoods.fulfilled, (state, action: PayloadAction<ICommentMoodsAPIResponse>) => {
      const apiResponse = action.payload;
      if (apiResponse) {
        state.apiResponse = apiResponse;
        const colorDictionary = {
          compliment: '#0493FC',
          suggestion: '#ffaa00',
          neutral: '#e7e7e7'
        }
        state.dataPoints = apiResponse.values.map((mood, index) => {
          return {
            color: colorDictionary[mood.label.toLowerCase()],
            count: mood.value,
            name: mood.label,
            selected: index === 0,
            surveyCount: mood.surveyCount,
            y: mood.percent
          }
        })
        const { avidLabel, surveyLabel, periodLabel, segment, segmentCategory, question, commentType } = apiResponse;
        state.chartSubtitle = buildHTMLFiltersSubtitle(
          avidLabel,
          surveyLabel,
          periodLabel,
          segment,
          segmentCategory,
          question,
          commentType
        )
        state.dashboardSubtitle = buildFiltersSubtitle(
          avidLabel,
          surveyLabel,
          periodLabel,
          segment,
          segmentCategory,
          question,
          commentType
        )
        state.isLoading = false;
      }
    });
  },
})

export const selectCommentMoods = (state: RootState) =>
  state.commentMoods;
export default commentMoodsSlice.reducer;
